import React, { useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    Paper,
    Popover,
    Checkbox,
    MenuItem,
    Collapse,
    Avatar,
    InputAdornment,
    IconButton,
    Switch,
    FormControlLabel
} from '@mui/material';
import { EmailOutlinedIcon, CalendarTodayOutlinedIcon, AccessTimeIcon } from 'icons/materialIcons';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import { useTeam } from 'team/contexts/teamContext';
import { getTeamSubteams } from 'team/apis/teamSubteamApi';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import TeamCalendarPopover from 'team/shared/components/date/TeamCalendarPopover';
import TeamTimePopover from 'team/shared/components/date/TeamTimePopover';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    isSameMonth,
    addMonths
} from 'date-fns';

const TeamNotificationsForm = ({
    notificationType,
    setNotificationType,
    recipients,
    recipientFilters = { subteams: [], members: [] },
    onRecipientFiltersChange,
    subject,
    setSubject,
    message,
    setMessage,
    scheduleEnabled,
    setScheduleEnabled,
    scheduleDate,
    setScheduleDate,
    scheduleTime,
    setScheduleTime,
    onSend,
    loading = false
}) => {
    const { currentTeamId } = useTeam();
    const [recipientAnchorEl, setRecipientAnchorEl] = useState(null);
    const [recipientSearch, setRecipientSearch] = useState('');
    const [subteamsList, setSubteamsList] = useState([]);
    const [membersList, setMembersList] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [localFilters, setLocalFilters] = useState(recipientFilters);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
    const [calendarViewDate, setCalendarViewDate] = useState(new Date(scheduleDate));
    const scheduleDateButtonRef = useRef(null);
    const [scheduleTimePopoverOpen, setScheduleTimePopoverOpen] = useState(false);
    const scheduleTimeButtonRef = useRef(null);

    const isRecipientPopoverOpen = Boolean(recipientAnchorEl);

    const generateCalendarDaysForSchedule = (date) => {
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(date);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);
        const days = [];
        let day = startDate;
        while (day <= endDate) {
            days.push({
                day: isSameMonth(day, monthStart) ? day.getDate() : null,
                date: isSameMonth(day, monthStart) ? new Date(day) : null
            });
            day = addDays(day, 1);
        }
        return days;
    };

    const scheduleCalendarDays = generateCalendarDaysForSchedule(calendarViewDate);
    const selectedScheduleDateObj = scheduleDate ? new Date(`${scheduleDate}T00:00:00`) : null;

    const handleOpenScheduleCalendar = (event) => {
        scheduleDateButtonRef.current = event.currentTarget;
        setCalendarViewDate(selectedScheduleDateObj || new Date());
        setSchedulePopoverOpen(true);
    };

    const handleScheduleDateSelect = (date) => {
        const formatted = format(date, 'yyyy-MM-dd');
        setScheduleDate(formatted);
        setSchedulePopoverOpen(false);
    };

    const handleOpenScheduleTime = (event) => {
        scheduleTimeButtonRef.current = event.currentTarget;
        setScheduleTimePopoverOpen(true);
    };

    const formatTimeWithMeridiem = (time24) => {
        if (!time24 || typeof time24 !== 'string' || !time24.includes(':')) return '';
        const [h, m] = time24.split(':');
        const hourNum = Number(h);
        if (Number.isNaN(hourNum)) return time24;
        const ampm = hourNum >= 12 ? 'PM' : 'AM';
        const hour12 = hourNum % 12 === 0 ? 12 : hourNum % 12;
        return `${String(hour12).padStart(2, '0')}:${m} ${ampm}`;
    };

    const memberOptions = useMemo(() => {
        return (membersList || []).map((member) => ({
            id: member._id || member.userId,
            userId: member.userId || member.user?.userId || member._id,
            name: member.user?.fullName || member.fullName || 'Unknown member',
            avatarUrl: member.defaultCard?.profilePictureUrl || member.user?.profilePictureUrl || member.profilePictureUrl || null,
            subteamIds: (member.subteamIds || []).map((id) => id?.toString?.() || id)
        }));
    }, [membersList]);

    const filteredSubteams = useMemo(() => {
        if (!recipientSearch.trim()) return subteamsList;
        const query = recipientSearch.toLowerCase();
        return subteamsList.filter((subteam) => (subteam.name || '').toLowerCase().includes(query));
    }, [subteamsList, recipientSearch]);

    const filteredMembers = useMemo(() => {
        if (!recipientSearch.trim()) return memberOptions;
        const query = recipientSearch.toLowerCase();
        return memberOptions.filter((member) => (member.name || '').toLowerCase().includes(query));
    }, [memberOptions, recipientSearch]);

    const computeRecipientUserIds = (filters) => {
        const selected = new Set();
        const selectedMemberIds = new Set(filters.members || []);
        const selectedSubteamIds = new Set((filters.subteams || []).map((id) => id.toString()));

        memberOptions.forEach((member) => {
            const inSelectedMember = selectedMemberIds.has(member.id);
            const inSelectedSubteam = member.subteamIds.some((id) => selectedSubteamIds.has(id.toString()));
            if (inSelectedMember || inSelectedSubteam) {
                selected.add(member.userId);
            }
        });

        return Array.from(selected);
    };

    const selectedRecipientMembers = useMemo(() => {
        const selectedMemberIds = new Set(recipientFilters.members || []);
        const selectedSubteamIds = new Set((recipientFilters.subteams || []).map((id) => id.toString()));
        return memberOptions.filter((member) => {
            const inSelectedMember = selectedMemberIds.has(member.id);
            const inSelectedSubteam = member.subteamIds.some((id) => selectedSubteamIds.has(id.toString()));
            return inSelectedMember || inSelectedSubteam;
        });
    }, [memberOptions, recipientFilters]);

    const fetchRecipientSources = async () => {
        if (!currentTeamId) return;
        setLoadingRecipients(true);
        try {
            const [subteamsResponse, membersResponse] = await Promise.all([
                getTeamSubteams(currentTeamId, { status: 'active', limit: 100 }),
                getTeamMembers(currentTeamId, { status: 'active', limit: 100 })
            ]);

            const subteams = subteamsResponse?.subteams || subteamsResponse || [];
            const members = membersResponse?.members || membersResponse || [];
            setSubteamsList(Array.isArray(subteams) ? subteams : []);
            setMembersList(Array.isArray(members) ? members : []);
        } catch (error) {
            console.error('Failed to load notification recipients:', error);
        } finally {
            setLoadingRecipients(false);
        }
    };

    const openRecipientPopover = async (event) => {
        setRecipientAnchorEl(event.currentTarget);
        setLocalFilters(recipientFilters);
        if (subteamsList.length === 0 && membersList.length === 0) {
            await fetchRecipientSources();
        }
    };

    const closeRecipientPopover = () => {
        setRecipientAnchorEl(null);
        setRecipientSearch('');
        setOpenDropdown(null);
    };

    const toggleSubteam = (subteamId) => {
        setLocalFilters((prev) => {
            const exists = prev.subteams.includes(subteamId);
            return {
                ...prev,
                subteams: exists ? prev.subteams.filter((id) => id !== subteamId) : [...prev.subteams, subteamId]
            };
        });
    };

    const toggleMember = (memberId) => {
        setLocalFilters((prev) => {
            const exists = prev.members.includes(memberId);
            return {
                ...prev,
                members: exists ? prev.members.filter((id) => id !== memberId) : [...prev.members, memberId]
            };
        });
    };

    const clearRecipientFilters = () => {
        setLocalFilters({ subteams: [], members: [] });
    };

    const handleRecipientOptionClick = (option, event) => {
        event.stopPropagation();
        setOpenDropdown(openDropdown === option ? null : option);
    };

    const applyRecipientFilters = () => {
        const recipientUserIds = computeRecipientUserIds(localFilters);
        onRecipientFiltersChange?.(localFilters, recipientUserIds);
        closeRecipientPopover();
    };

    // Style for the toggle buttons
    const getButtonStyle = (type) => ({
        textTransform: 'none',
        borderRadius: '20px',
        padding: '8px 24px',
        fontWeight: 'bold',
        backgroundColor: notificationType === type ? '#000' : '#f5f5f5',
        color: notificationType === type ? '#fff' : '#757575',
        border: 'none',
        '&:hover': {
            backgroundColor: notificationType === type ? '#333' : '#e0e0e0',
        },
        marginRight: '12px',
        boxShadow: 'none',
    });

    return (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, backgroundColor: 'white', maxWidth: '800px', mx: 'auto' }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                    {notificationType === 'email' ? 'Send Email Notification' : 'Send Push Notification'}
                </Typography>

                {/* Toggle Buttons */}
                <Box sx={{ display: 'flex', mb: 4 }}>
                    <Button
                        variant="contained"
                        onClick={() => setNotificationType('push')}
                        sx={getButtonStyle('push')}
                        startIcon={<NotificationsOutlinedIcon />}
                        disableElevation
                    >
                        Push notification
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setNotificationType('email')}
                        sx={getButtonStyle('email')}
                        startIcon={<EmailOutlinedIcon />}
                        disableElevation
                    >
                        Email
                    </Button>
                </Box>
                {notificationType === 'push' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                        Recipients can enable push notifications in Settings → Team Settings.
                    </Typography>
                )}

                {/* Recipients Field */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Recipients *
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder={selectedRecipientMembers.length === 0 ? "Search recipient" : ""}
                        value=""
                        onClick={openRecipientPopover}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <InputAdornment
                                    position="start"
                                    sx={{
                                        maxWidth: '100%',
                                        width: '100%',
                                        mr: 0
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, width: '100%' }}>
                                        <IconButton edge="start" size="small" onClick={openRecipientPopover} sx={{ p: 0.5 }}>
                                            <SearchIcon fontSize="small" />
                                        </IconButton>
                                        {selectedRecipientMembers.map((member) => (
                                            <Box
                                                key={member.userId}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    px: 1,
                                                    py: 0.35,
                                                    borderRadius: '14px',
                                                    backgroundColor: '#efefef',
                                                    maxWidth: 180
                                                }}
                                            >
                                                <Avatar
                                                    src={member.avatarUrl || undefined}
                                                    sx={{ width: 20, height: 20, backgroundColor: '#d8d8d8', fontSize: '0.7rem' }}
                                                >
                                                    {!member.avatarUrl && member.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#333', maxWidth: 130 }} noWrap>
                                                    {member.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            backgroundColor: '#f9f9f9',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiInputBase-root': { alignItems: 'flex-start', py: 0.4, minHeight: 40 },
                            '& .MuiInputBase-input': { display: 'none' }
                        }}
                    />
                </Box>

                <Popover
                    open={isRecipientPopoverOpen}
                    anchorEl={recipientAnchorEl}
                    onClose={closeRecipientPopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            borderRadius: '12px',
                            minWidth: 320,
                            maxWidth: 420,
                            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                            border: '1px solid #e0e0e0',
                            p: 2
                        }
                    }}
                >
                    <Box sx={{
                        mb: 2,
                        '& .MuiInputBase-root': {
                            fontSize: '0.7rem',
                            '& .MuiInputBase-input::placeholder': {
                                color: '#A0A0A0',
                                opacity: 1
                            }
                        }
                    }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search subteams or members..."
                            value={recipientSearch}
                            onChange={(e) => setRecipientSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: '20px',
                                    '& fieldset': { border: 'none' }
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        {[{ key: 'subteams', label: 'Subteams' }, { key: 'members', label: 'Members' }].map((option) => {
                            const selectedCount = Array.isArray(localFilters[option.key]) ? localFilters[option.key].length : 0;
                            const isDropdownOpen = openDropdown === option.key;

                            return (
                                <Box key={option.key} sx={{ marginBottom: '0.75rem' }}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            borderRadius: '0.25rem',
                                            padding: '0.75rem 1rem',
                                            minHeight: '3rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#f0f0f0' }
                                        }}
                                        onClick={(e) => handleRecipientOptionClick(option.key, e)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#666666', lineHeight: 1.5 }}>
                                                {option.label}
                                            </Typography>
                                            {selectedCount > 0 && (
                                                <Typography sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                    color: '#1976d2',
                                                    backgroundColor: '#e3f2fd',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px'
                                                }}>
                                                    {selectedCount}
                                                </Typography>
                                            )}
                                        </Box>
                                        <KeyboardArrowDownOutlinedIcon
                                            sx={{
                                                fontSize: '1.25rem',
                                                color: '#666',
                                                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease-in-out'
                                            }}
                                        />
                                    </Box>

                                    <Collapse in={isDropdownOpen} timeout="auto" unmountOnExit>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                mt: 0.5,
                                                maxHeight: 260,
                                                width: '100%',
                                                borderRadius: '8px',
                                                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                                                border: '1px solid #e0e0e0',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {option.key === 'subteams' ? (
                                                loadingRecipients ? (
                                                    <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: '#666' }}>Loading...</Typography></MenuItem>
                                                ) : filteredSubteams.length === 0 ? (
                                                    <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: '#666' }}>No subteams found</Typography></MenuItem>
                                                ) : (
                                                    filteredSubteams.map((subteam) => {
                                                        const isSelected = localFilters.subteams.includes(subteam._id);
                                                        return (
                                                            <MenuItem
                                                                key={subteam._id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleSubteam(subteam._id);
                                                                }}
                                                                sx={{ padding: '0.75rem 1rem', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    sx={{
                                                                        color: '#e0e0e0',
                                                                        padding: 0,
                                                                        marginRight: '12px',
                                                                        '&.Mui-checked': { color: '#000' }
                                                                    }}
                                                                />
                                                                <Avatar sx={{ width: 32, height: 32, marginRight: '12px', backgroundColor: '#e0e0e0' }}>
                                                                    {(subteam.name || 'S').charAt(0).toUpperCase()}
                                                                </Avatar>
                                                                <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>
                                                                    {subteam.name || 'Unnamed Subteam'}
                                                                </Typography>
                                                            </MenuItem>
                                                        );
                                                    })
                                                )
                                            ) : (
                                                loadingRecipients ? (
                                                    <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: '#666' }}>Loading...</Typography></MenuItem>
                                                ) : filteredMembers.length === 0 ? (
                                                    <MenuItem disabled><Typography sx={{ fontSize: '0.8rem', color: '#666' }}>No members found</Typography></MenuItem>
                                                ) : (
                                                    filteredMembers.map((member) => {
                                                        const isSelected = localFilters.members.includes(member.id);
                                                        return (
                                                            <MenuItem
                                                                key={member.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleMember(member.id);
                                                                }}
                                                                sx={{ padding: '0.75rem 1rem', '&:hover': { backgroundColor: '#f5f5f5' } }}
                                                            >
                                                                <Checkbox
                                                                    checked={isSelected}
                                                                    sx={{
                                                                        color: '#e0e0e0',
                                                                        padding: 0,
                                                                        marginRight: '12px',
                                                                        '&.Mui-checked': { color: '#000' }
                                                                    }}
                                                                />
                                                                <Avatar sx={{ width: 32, height: 32, marginRight: '12px', backgroundColor: '#e0e0e0' }}>
                                                                    {(member.name || 'M').charAt(0).toUpperCase()}
                                                                </Avatar>
                                                                <Typography sx={{ fontSize: '0.875rem', color: '#333', flex: 1 }}>
                                                                    {member.name}
                                                                </Typography>
                                                            </MenuItem>
                                                        );
                                                    })
                                                )
                                            )}
                                        </Paper>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={clearRecipientFilters}
                            sx={{
                                backgroundColor: 'white',
                                color: '#828282',
                                borderRadius: '20px',
                                border: 'none',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': { backgroundColor: 'white', boxShadow: 2 }
                            }}
                        >
                            Clear filter
                        </Button>
                        <Button
                            variant="contained"
                            onClick={applyRecipientFilters}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': { backgroundColor: '#333' }
                            }}
                        >
                            Apply filter
                        </Button>
                    </Box>
                </Popover>

                {/* Subject/Title Field */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        {notificationType === 'email' ? 'Subject *' : 'Title *'}
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder={notificationType === 'email' ? 'Subject' : 'Title'}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ backgroundColor: '#f9f9f9', '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                    />
                </Box>

                {/* Message Field */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Message *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9f9f9', '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                    />
                </Box>

                {/* Schedule Fields */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Schedule
                        </Typography>
                        <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                                <Switch
                                    checked={scheduleEnabled}
                                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#000' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#000'
                                        }
                                    }}
                                />
                            }
                            label={scheduleEnabled ? 'On' : 'Off'}
                            labelPlacement="start"
                        />
                    </Box>
                    {scheduleEnabled ? (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Button
                                    ref={scheduleDateButtonRef}
                                    variant="contained"
                                    startIcon={<CalendarTodayOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                    onClick={handleOpenScheduleCalendar}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#4f4f4f',
                                        borderRadius: '20px',
                                        border: '1px solid #e3e3e3',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            boxShadow: 2
                                        }
                                    }}
                                >
                                    {selectedScheduleDateObj ? format(selectedScheduleDateObj, 'MMM dd, yyyy') : 'Select date'}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    ref={scheduleTimeButtonRef}
                                    variant="contained"
                                    startIcon={<AccessTimeIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                    onClick={handleOpenScheduleTime}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#4f4f4f',
                                        borderRadius: '20px',
                                        border: '1px solid #e3e3e3',
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                            boxShadow: 2
                                        }
                                    }}
                                >
                                    {scheduleTime ? formatTimeWithMeridiem(scheduleTime) : 'Select time'}
                                </Button>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography variant="body2" sx={{ color: '#828282', fontSize: '0.75rem' }}>
                            Sends immediately when turned off.
                        </Typography>
                    )}
                </Box>

                <TeamCalendarPopover
                    open={schedulePopoverOpen}
                    anchorEl={scheduleDateButtonRef.current}
                    onClose={() => setSchedulePopoverOpen(false)}
                    title="Select Schedule Date"
                    mode="single"
                    calendarViewDate={calendarViewDate}
                    onPrevMonth={() => setCalendarViewDate((prev) => addMonths(prev, -1))}
                    onNextMonth={() => setCalendarViewDate((prev) => addMonths(prev, 1))}
                    calendarDays={scheduleCalendarDays}
                    onDateSelect={handleScheduleDateSelect}
                    selectedDate={selectedScheduleDateObj}
                />

                <TeamTimePopover
                    open={scheduleTimePopoverOpen}
                    anchorEl={scheduleTimeButtonRef.current}
                    onClose={() => setScheduleTimePopoverOpen(false)}
                    title="Select Schedule Time"
                    value={scheduleTime}
                    onChange={setScheduleTime}
                />

                {/* Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: '#e0e0e0',
                            color: '#757575',
                            textTransform: 'none',
                            borderRadius: '20px',
                            fontWeight: 'bold',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#d5d5d5',
                                boxShadow: 'none'
                            },
                            px: 4,
                            height: '38px',
                            fontSize: '0.875rem'
                        }}
                    >
                        Cancel
                    </Button>
                    <DarkButtonWithIcon
                        text={loading ? 'Sending...' : `Send ${notificationType === 'email' ? 'Email' : 'Notification'}`}
                        onClick={onSend}
                        disabled={loading}
                        sx={{
                            px: 4,
                            height: '38px',
                            fontSize: '0.875rem',
                            opacity: loading ? 0.7 : 1,
                            backgroundColor: loading ? '#666' : '#000'
                        }}
                    />
                </Box>

            </Box>
        </Paper>
    );
};

export default TeamNotificationsForm;
